import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft } from '../../icons';
import { Button, Tabs } from '../../components/shared';
import {
  fetchPersonCard, SCHEDULE_SOURCE_LABEL, type PersonCardDto,
} from '../../api/hr-people';
import { WORK_TYPE_LABEL } from '../../api/hr-schedule';
import { StaffRecordSection, RECORD_TABS, type RecordTab } from './StaffRecordSection';
import { PersonHead, type PersonFact } from './PersonHead';
import './MyRecordCardPage.css';
import './StaffCard.css';

/**
 * **인사 사람 한 장** — 담당자가 보는 한 사람의 전부 (2026-08-30 · 판 B2).
 *
 * ## 무엇이 없었나
 *
 * ★★ 직원 명부에서 사람을 눌러도 **오른쪽 칸에 계정 몇 줄**이 뜨는 것이 전부였다. 발령·경력·
 *    포상·평가·교육은 **본인만** 볼 수 있었다(내 인사기록카드). 그래서 담당자가 한 사람을
 *    보려면 화면 다섯을 돌아다녀야 했다 — 그런데 그 화면들은 **사람으로 묶여 있지 않다.**
 *
 * ## 서버는 이미 다 있었다
 *
 * ★ `GET /hr/people/:id` 도, 탭마다 부르는 라우트도 **전부 userId 를 받는다.** 없던 것은
 *   **그것을 한 장으로 세우는 화면**뿐이었다. 그래서 서버 0 · 마이그레이션 0 이다.
 *
 * ## 보는 만큼만 온다
 *
 * ★★ 범위 판정은 **서버가 한다**(`scope`) — 담당자는 `ALL`, 팀장은 자기 팀만 `TEAM`,
 *    나머지는 `PUBLIC` 이라 개인 칸이 아예 안 온다. 화면은 **온 것만 그린다**:
 *    없는 칸을 「—」로 그리면 「비어 있다」로 읽혀 담당자가 채우러 간다.
 *
 * ## ★ 열면 기록이 남는다
 *
 * 서버가 `hr.person.viewed` 를 남긴다(본인·공개 카드는 안 남긴다). 화면이 그 사실을 **적는다** —
 * 남는다는 것을 모르면 남기는 뜻의 절반이 없다.
 */

/** ★ 기록 다섯은 공용 목록을 그대로 쓴다 — 이름이 두 화면에서 갈리면 안 된다. */
type Tab = 'PROFILE' | RecordTab;

/**
 * ★ 첫 칸은 **「인사 정보」** 다(2026-09-02 · 사용자 결정) — 「신상」은 무엇이 들었는지
 *   말하지 않는 말이었다. 여기에 고용형태 · 근무제 · 사번 · 입사일 · 근속을 다 세운다.
 */
const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: 'PROFILE', label: '인사 정보' },
  ...RECORD_TABS,
];

const SCOPE_NOTE: Record<string, string> = {
  ALL: '인사 담당 자격으로 봅니다 — 이 열람은 기록에 남습니다.',
  TEAM: '팀장 자격으로 봅니다(내 팀) — 이 열람은 기록에 남습니다.',
  SELF: '본인 카드입니다.',
  PUBLIC: '조직도에 공개된 것만 보입니다 — 인사 정보는 담당자만 봅니다.',
};

export function StaffCardPage(): JSX.Element {
  const { userId = '' } = useParams();
  const [tab, setTab] = useState<Tab>('PROFILE');
  const [card, setCard] = useState<PersonCardDto | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');

  const load = useCallback(async () => {
    const c = await fetchPersonCard(userId);
    setCard(c);
  }, [userId]);

  useEffect(() => {
    let alive = true;
    setState('loading');
    void (async () => {
      try {
        const c = await fetchPersonCard(userId);
        if (alive) { setCard(c); setState('ok'); }
      } catch {
        if (alive) setState('error');
      }
    })();
    return () => { alive = false; };
  }, [userId]);


  if (state === 'loading') {
    return <div className="hrpage"><p className="hrpage__empty">불러오는 중…</p></div>;
  }
  if (state === 'error' || !card) {
    return (
      <div className="hrpage">
        <p className="hrcert__err" role="alert">
          <AlertCircle size={14} aria-hidden />
          그 사람을 볼 수 없습니다 — 없는 사람이거나 볼 권한이 없습니다.
        </p>
        <Link className="hrjg__link" to="/erp/hr/staff">직원 명부로</Link>
      </div>
    );
  }

  const p = card.person;
  const wide = card.scope === 'ALL' || card.scope === 'TEAM' || card.scope === 'SELF';
  /**
   * 사진을 바꿀 수 있나 — **인사 담당(`ALL`) 과 본인(`SELF`)**.
   * ★ 서버가 하는 판정과 **같은 말**이어야 한다. 처음 판은 `ALL` 만 봐서, 담당자가 제 카드를
   *   열면 **직원 관리에서는 바꿀 수 있는데 인사 카드에서는 단추가 없었다** — 같은 사람을
   *   두 화면에서 열었는데 되는 일이 달랐다(운영에서 찔러 보고 잡았다).
   * ★ 팀장(`TEAM`)은 못 바꾼다 — 팀원의 사진을 바꾸는 것은 팀을 이끄는 일이 아니다.
   */
  const canEditAvatar = card.scope === 'ALL' || card.scope === 'SELF';

  /** 머리에 늘 두는 것 — **연락과 자리**. 인사 사실은 「인사 정보」 탭이 맡는다. */
  const headFacts: PersonFact[] = [
    { label: '메일', value: p.email },
    ...(p.phone !== undefined ? [{ label: '휴대전화', value: p.phone ?? '—', numeric: true }] : []),
    ...(p.extension !== undefined ? [{ label: '내선', value: p.extension ?? '—', numeric: true }] : []),
    ...(p.deskLocation !== undefined ? [{ label: '자리', value: p.deskLocation ?? '—' }] : []),
  ];

  const sch = card.schedule ?? null;

  /**
   * 「인사 정보」 — **다 보여 준다**(2026-09-02 · 사용자 결정).
   * ★ 없는 칸은 **줄을 안 만든다.** 범위 밖이면 서버가 키를 안 주고, 「—」로 그리면
   *   「비어 있다」로 읽혀 담당자가 채우러 간다.
   */
  const hrRows: Array<[string, React.ReactNode]> = [
    ['이름', `${p.name}${p.nameEn ? ` (${p.nameEn})` : ''}`],
    ...(p.employeeNumber !== undefined ? [['사번', p.employeeNumber ?? '—'] as [string, React.ReactNode]] : []),
    ['소속', [p.divisionName, p.teamName].filter(Boolean).join(' · ') || '회사 직속'],
    ['직위', p.position ?? '—'],
    ...(p.jobLevel !== undefined ? [['직급', p.jobLevel == null ? '—' : `${p.jobLevel}급`] as [string, React.ReactNode]] : []),
    ...(p.employmentType !== undefined ? [['고용형태', p.employmentType ?? '—'] as [string, React.ReactNode]] : []),
    ['재직상태', p.employmentStatus ?? '—'],
    ...(p.hireDate !== undefined ? [['입사일', p.hireDate ?? '—'] as [string, React.ReactNode]] : []),
    ...(p.tenureLabel !== undefined ? [['근속', p.tenureLabel ?? '—'] as [string, React.ReactNode]] : []),
    ...(sch ? [['근무제', (
      <span className="stfc__sch">
        <b>{sch.name ?? '회사 기본'}</b>
        {/* ★ 한 조각으로 낸다 — 나눠 쓰면 텍스트 마디가 셋으로 쪼개져 「09:00–18:00」을
               한 낱말로 찾을 수 없다(읽어 주는 쪽도 끊어 읽는다). */}
        <span className="hrpage__pill">{`${sch.baseStart}–${sch.baseEnd}`}</span>
        {/* ★ 종류가 이름과 **같은 낱말이면 적지 않는다** — 「통상근무 · 통상근무」가 된다.
               다섯 중 넷이 그렇다(통상근무·시차출퇴근·선택근로·탄력근로). #937 에서 겪은 것과 같다. */}
        {sch.type && (WORK_TYPE_LABEL[sch.type] ?? sch.type) !== sch.name && (
          <span className="hrpage__dim">{WORK_TYPE_LABEL[sch.type] ?? sch.type}</span>
        )}
        {/* ★★ 「무엇이 걸렸나」만으로는 모자란다 — 개인 배정이 없어도 팀·회사 기본으로
               이미 적용되고 있다. 출처를 안 적으면 「비었다」로 읽힌다. */}
        <span className="hrpage__pill stfc__schsrc">{SCHEDULE_SOURCE_LABEL[sch.source] ?? sch.source}</span>
      </span>
    )] as [string, React.ReactNode]] : []),
    ...(p.birthday !== undefined ? [['생일', p.birthday ?? '—'] as [string, React.ReactNode]] : []),
  ];

  return (
    <div className="hrpage stfc">
      <PersonHead
        userId={p.userId}
        name={p.name}
        avatarUrl={p.avatarUrl}
        canEditAvatar={canEditAvatar}
        onAvatarChange={() => { void load(); }}
        back={(
          <Link className="stfc__back" to="/erp/hr/staff">
            <ArrowLeft size={14} aria-hidden />직원 명부
          </Link>
        )}
        badges={(
          <>
            {p.employmentStatus && <span className="hrpage__pill stfc__pill">{p.employmentStatus}</span>}
            {p.isLeader && <span className="hrpage__pill stfc__pill">팀장</span>}
            {p.employmentType && <span className="hrpage__pill">{p.employmentType}</span>}
          </>
        )}
        subtitle={[
          [p.divisionName, p.teamName].filter(Boolean).join(' · ') || '회사 직속',
          p.position,
          p.employeeNumber ? `사번 ${p.employeeNumber}` : null,
          p.tenureLabel ? `근속 ${p.tenureLabel}` : null,
        ].filter(Boolean).join(' · ')}
        facts={headFacts}
        actions={wide ? (
          <>
            {/* ★ 이 화면에서 하는 일이 아니라 **가는 길**이다. */}
            <Button variant="secondary" size="compact" href="/erp/hr/assignments">인사발령 등록</Button>
            <Button variant="secondary" size="compact" href="/erp/hr/certificates">증명서</Button>
          </>
        ) : undefined}
        /* ★ 열면 기록이 남는다 — 남는다는 것을 모르면 남기는 뜻의 절반이 없다. */
        note={SCOPE_NOTE[card.scope] ?? ''}
      />

      {/* ★ 정본 E5 의 `line` — 다른 내용으로 가는 주 탭이다(필터가 아니다).
             아래 줄이 「위는 사람, 아래는 기록」을 가르는 경계선을 겸한다. */}
      <Tabs
        className="stfc__tabs"
        ariaLabel="인사기록"
        items={TABS.map((t) => ({ value: t.key, label: t.label }))}
        value={tab}
        onChange={(v) => setTab(v as Tab)}
      />

      {tab === 'PROFILE' && (
        <section className="hrpage__panel">
          <h2 className="hrpage__panelt">
            인사 정보
            <span className="hrpage__panels">보는 범위만큼만 옵니다</span>
          </h2>
          <table className="hrpage__tbl">
            <tbody>
              {hrRows.map(([k, v]) => (
                <tr key={k}><td className="hrcard__k">{k}</td><td>{v}</td></tr>
              ))}
            </tbody>
          </table>
          {!wide && (
            <div className="hrpage__note">
              조직도에 공개된 것만 보입니다 — 인사 정보는 담당자와 그 사람의 팀장이 봅니다.
            </div>
          )}
          {wide && !sch && (
            <div className="hrpage__note">
              근무제를 읽지 못했습니다 — <b>근태 › 근무제</b>에서 배정을 확인해 주세요.
            </div>
          )}
        </section>
      )}

      {/*
        ★★★ **기록 다섯은 공용 한 벌이다** — 직원 관리의 상세도 같은 것을 그린다.
           복사해 두면 한쪽만 고쳐지는 날이 온다.
      */}
      {tab !== 'PROFILE' && <StaffRecordSection userId={userId} tab={tab} />}
    </div>
  );
}

export default StaffCardPage;
